import { useState } from 'react'
import { StaticPageLayout } from '../layouts/StaticPageLayout'
import { FormInput, FormSelect, FormTextarea, FormButton } from '../components/forms'
import { Alert, H2, H3, Link } from '../components/ui'
import { SEO } from '../components/SEO/SEO'
import { useAnalytics } from '../hooks/analytics/useAnalytics'

export const Contact = () => {
  const { trackEvent, trackError } = useAnalytics()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [formState, setFormState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null,
  })
  const [fieldErrors, setFieldErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: null,
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.name.trim()) errors.name = 'Name is required'
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid'
    }
    if (!formData.subject.trim()) errors.subject = 'Subject is required'
    if (!formData.message.trim()) {
      errors.message = 'Message is required'
    } else if (formData.message.length < 10) {
      errors.message = 'Message must be at least 10 characters'
    }

    setFieldErrors(errors)

    // Track form validation errors
    if (Object.keys(errors).length > 0) {
      trackEvent('contact_form_validation_error', {
        error_fields: Object.keys(errors),
        error_count: Object.keys(errors).length,
        subject_type: formData.subject || 'none',
      })
    }

    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      setFormState((prev) => ({ ...prev, error: 'Please fix the errors below' }))
      return
    }

    setFormState({
      isSubmitting: true,
      isSubmitted: false,
      error: null,
    })

    try {
      // Send form data to the contact API endpoint
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message')
      }

      // Track successful form submission
      trackEvent('contact_form_success', {
        subject_type: formData.subject,
        message_length: formData.message.length,
        response_status: response.status,
      })

      setFormState({
        isSubmitting: false,
        isSubmitted: true,
        error: null,
      })

      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      })
    } catch (error) {
      console.error('Contact form error:', error)

      // Track contact form error
      trackError(`Contact form submission failed: ${error.message}`, false)
      trackEvent('contact_form_error', {
        error_message: error.message,
        subject_type: formData.subject,
        error_type: 'submission_failed',
      })

      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error:
          error.message ||
          'Failed to send message. Please try again or contact us directly at support@etai.app.',
      })
    }
  }

  const seoConfig = {
    title: 'Contact Et Ai - AI Research Tool Support',
    description:
      'Get in touch with Et Ai for enquiries about our factually accurate AI research assistant and academic AI solutions. Technical support, partnerships, and business enquiries.',
    keywords:
      'contact Et Ai, AI research support, academic AI help, Et Ai beta, technical support, business enquiries',
    url: 'https://etai.app/contact',
    canonical: 'https://etai.app/contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'Et Ai Labs',
        url: 'https://etai.app',
        email: 'support@etai.app',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '26 Lower River Terrace',
          addressLocality: 'South Brisbane',
          addressRegion: 'QLD',
          postalCode: '4101',
          addressCountry: 'AU',
        },
        contactPoint: [
          {
            '@type': 'ContactPoint',
            email: 'support@etai.app',
            contactType: 'customer support',
            availableLanguage: 'English',
          },
          {
            '@type': 'ContactPoint',
            email: 'contact@etai.app',
            contactType: 'business development',
            availableLanguage: 'English',
          },
        ],
      },
    },
  }

  return (
    <StaticPageLayout title="Contact Us">
      <SEO {...seoConfig} />
      <div className="grid gap-8 lg:grid-cols-2">
        {/* Contact Form */}
        <div className="space-y-8">
          <div className="bg-dark-secondary rounded-lg border border-dark-border p-8">
            <H2 className="mb-6">Send us a message</H2>

            {formState.isSubmitted && (
              <Alert type="success" title="Message sent successfully!" className="mb-6">
                Thank you for contacting us. We&apos;ll get back to you within 24-48 hours.
              </Alert>
            )}

            {formState.error && (
              <Alert type="error" title="Error" message={formState.error} className="mb-6" />
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormInput
                  id="name"
                  name="name"
                  type="text"
                  label="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  required
                  error={fieldErrors.name}
                  disabled={formState.isSubmitting}
                />
                <FormInput
                  id="email"
                  name="email"
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  required
                  error={fieldErrors.email}
                  disabled={formState.isSubmitting}
                />
              </div>

              <FormSelect
                id="subject"
                name="subject"
                label="Subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                error={fieldErrors.subject}
                disabled={formState.isSubmitting}
              >
                <option value="">Select a subject</option>
                <option value="general">General Enquiry</option>
                <option value="support">Technical Support</option>
                <option value="feature">Feature Request</option>
                <option value="bug">Bug Report</option>
                <option value="partnership">Partnership</option>
                <option value="billing">Billing & Accounts</option>
                <option value="other">Other</option>
              </FormSelect>

              <FormTextarea
                id="message"
                name="message"
                label="Message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us how we can help you..."
                rows={6}
                maxLength={500}
                required
                error={fieldErrors.message}
                disabled={formState.isSubmitting}
              />

              <FormButton
                type="submit"
                variant="primary"
                loading={formState.isSubmitting}
                disabled={formState.isSubmitting}
                fullWidth
              >
                Send Message
              </FormButton>
            </form>
          </div>

          <div className="bg-dark-secondary rounded-lg border border-dark-border p-8">
            <H2 className="mb-6">Alternative Support</H2>
            <div className="space-y-4">
              <div>
                <H3 className="mb-2">Help Center</H3>
                <p className="text-gray-400 text-sm mb-3">Find answers to common questions.</p>
                <Link to="/help" size="sm">
                  Visit Help Center →
                </Link>
              </div>
              <div>
                <H3 className="mb-2">Ask Et Ai</H3>
                <p className="text-gray-400 text-sm mb-3">
                  You can chat with Et Ai for help and usage tips without any sign-up!
                </p>
                <Link to="/" size="sm">
                  Go to Chat Interface →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-8">
          <div className="bg-dark-secondary rounded-lg border border-dark-border p-8">
            <H2 className="mb-6">Get in touch</H2>
            <div className="space-y-6">
              <div>
                <H3 className="mb-2">Discord Server</H3>
                <div className="space-y-2">
                  <Link href="https://discord.gg/txuZbDUzdx" className="block">
                    Join our Discord
                  </Link>
                  <p className="text-gray-400 text-sm">
                    The fastest way to get support, connect with the community, and join the beta
                    program
                  </p>
                </div>
              </div>

              <div>
                <H3 className="mb-2">Email Support</H3>
                <div className="space-y-2">
                  <Link href="mailto:support@etai.app" className="block">
                    support@etai.app
                  </Link>
                  <p className="text-gray-400 text-sm">
                    For technical support and general enquiries
                  </p>
                </div>
              </div>

              <div>
                <H3 className="mb-2">Business Enquiries</H3>
                <div className="space-y-2">
                  <Link href="mailto:contact@etai.app" className="block">
                    contact@etai.app
                  </Link>
                  <p className="text-gray-400 text-sm">For partnerships and business development</p>
                </div>
              </div>

              <div>
                <H3 className="mb-2">Privacy & Security</H3>
                <div className="space-y-2">
                  <Link href="mailto:privacy@etai.app" className="block">
                    privacy@etai.app
                  </Link>
                  <p className="text-gray-400 text-sm">For data privacy and security concerns</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-secondary rounded-lg border border-dark-border p-8">
            <H2 className="mb-6">Office Location</H2>
            <div className="space-y-3">
              <p className="text-white font-medium">Et Ai Labs Pty Ltd</p>
              <div className="text-gray-300">
                <p>26 Lower River Terrace</p>
                <p>South Brisbane, QLD 4101</p>
                <p>Australia</p>
              </div>
            </div>
          </div>

          <div className="bg-dark-secondary rounded-lg border border-dark-border p-8">
            <H2 className="mb-6">Response Times</H2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">General Enquiries</span>
                <span className="text-white font-medium">24-48 hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Technical Support</span>
                <span className="text-white font-medium">12-24 hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Urgent Issues</span>
                <span className="text-white font-medium">2-6 hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Business Enquiries</span>
                <span className="text-white font-medium">1-3 business days</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  )
}
